import type { Directive, DirectiveBinding } from 'vue'

export interface RevealOptions {
  /** 노출 지연(ms). 카드 목록 stagger 용 */
  delay?: number
}

type RevealBinding = number | RevealOptions | undefined

const REVEAL_ATTR = 'data-reveal'
const VISIBLE_CLASS = 'is-in'
/** 관찰자가 놓친 요소를 회수하는 최종 안전장치 시점 */
const FAILSAFE_MS = 1200

const delayMap = new WeakMap<Element, number>()
const pending = new Set<Element>()

let observer: IntersectionObserver | null = null

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false
}

function reveal(el: Element) {
  if (!pending.has(el)) return
  pending.delete(el)
  observer?.unobserve(el)

  const delay = delayMap.get(el) ?? 0
  if (delay > 0) {
    window.setTimeout(() => el.classList.add(VISIBLE_CLASS), delay)
  } else {
    el.classList.add(VISIBLE_CLASS)
  }
}

/** 뷰포트에 조금이라도 걸쳐 있는지 (화면보다 큰 요소 포함) */
function isInViewport(el: Element): boolean {
  const rect = el.getBoundingClientRect()
  const height = window.innerHeight || document.documentElement.clientHeight
  return rect.top < height && rect.bottom > 0
}

function getObserver(): IntersectionObserver | null {
  if (observer) return observer
  if (typeof IntersectionObserver === 'undefined') return null

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target)
      })
    },
    // threshold 0 — 화면보다 큰 섹션도 걸치는 순간 노출된다.
    // (비율 기준이면 전체가 화면에 담기지 않아 영영 트리거되지 않는다.)
    // rootMargin 하단을 +로 두어, 요소가 화면에 들어오기 전에 미리 노출한다.
    // (스크롤 시 제목만 보이고 본문이 빈 공간으로 남는 현상을 막는다.)
    { threshold: 0, rootMargin: '0px 0px 22% 0px' },
  )

  return observer
}

function parseDelay(binding: RevealBinding): number {
  if (typeof binding === 'number') return binding
  return binding?.delay ?? 0
}

/**
 * 요소를 리빌 대상으로 등록한다.
 * 지시어를 쓰지 않고 [data-reveal] 마크업을 직접 두는 화면에서 사용한다.
 */
export function registerReveal(el: Element, delay = 0) {
  if (prefersReducedMotion()) {
    el.classList.add(VISIBLE_CLASS)
    return
  }

  delayMap.set(el, delay)
  pending.add(el)

  const io = getObserver()
  if (!io) {
    reveal(el)
    return
  }

  // 관찰자는 이미 화면에 걸친 요소에 대해서도 최초 1회 콜백을 준다.
  io.observe(el)

  // rootMargin 경계에 걸리거나 콜백을 놓친 요소를 회수한다.
  window.setTimeout(() => {
    if (pending.has(el) && isInViewport(el)) reveal(el)
  }, FAILSAFE_MS)
}

/** 컨테이너 내부의 [data-reveal] 요소를 일괄 등록한다. */
export function registerRevealTargets(root: ParentNode | null | undefined) {
  if (!root) return
  root.querySelectorAll('[data-reveal]').forEach((el) => registerReveal(el))
}

export const vReveal: Directive<HTMLElement, RevealBinding> = {
  // DOM 삽입 전에 초기 상태를 심어 첫 페인트 깜빡임을 막는다.
  created(el, binding: DirectiveBinding<RevealBinding>) {
    if (prefersReducedMotion()) return
    el.setAttribute(REVEAL_ATTR, '')
    delayMap.set(el, parseDelay(binding.value))
  },

  mounted(el, binding: DirectiveBinding<RevealBinding>) {
    if (!el.hasAttribute(REVEAL_ATTR)) return
    registerReveal(el, parseDelay(binding.value))
  },

  unmounted(el) {
    pending.delete(el)
    observer?.unobserve(el)
  },
}
